/**
 * A Arquitetura de Onboarding — a landing page pessoal que o cliente recebe
 * por um link sem login. Território escuro (a marca dela), no clima de
 * direção de cinema que ela pediu. Só leitura: a rota pública não aceita
 * escrita, então não existe "editar" nem por tentativa direta na API.
 * Fonte: pedido de 26/jul.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { OnboardingContent } from "@unik/shared";
import { api } from "../lib/api";
import { BrandMark } from "../ui/BrandMark";
import { Theme } from "../ui/Theme";

interface ArchitectureData {
  content: OnboardingContent;
  clientName: string | null;
  currentStep: number;
}

interface Props {
  /** Link de visualização (sem cliente atrelado) em vez do link pessoal do cliente. */
  preview?: boolean;
}

function useScrollProgress(): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    function onScroll() {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? Math.min(1, h.scrollTop / max) : 0);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return p;
}

export function OnboardingArchitecture({ preview = false }: Props) {
  const { token } = useParams();
  const [data, setData] = useState<ArchitectureData | null>(null);
  const [error, setError] = useState(false);
  const progress = useScrollProgress();

  useEffect(() => {
    const path = preview ? `/public/architecture-preview/${token}` : `/public/architecture/${token}`;
    api
      .get<ArchitectureData>(path)
      .then(setData)
      .catch(() => setError(true));
  }, [token, preview]);

  if (error) {
    return (
      <Theme territory="admin">
        <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-6 text-center">
          <BrandMark variant="creme" className="h-7" />
          <h1 className="type-h2 mt-4 text-text-cream">Este link não está mais disponível</h1>
          <p className="type-body mt-2 max-w-sm text-text-secondary">
            Peça um novo link pra Lara, ou fala com ela pelo WhatsApp.
          </p>
        </div>
      </Theme>
    );
  }

  if (data === null) {
    return (
      <Theme territory="admin">
        <div className="app-shell flex min-h-dvh items-center justify-center">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-text-tertiary border-t-transparent" />
        </div>
      </Theme>
    );
  }

  const { content, clientName, currentStep } = data;
  const firstName = clientName?.split(" ")[0] ?? null;

  return (
    <Theme territory="admin">
      <div className="app-shell min-h-dvh">
        {/* Barra de progresso — "onde eu estou na leitura", o próprio documento como roadmap */}
        <div className="fixed left-0 right-0 top-0 z-[var(--z-sticky)] h-[3px] bg-[var(--border-subtle)]">
          <div
            className="h-full bg-[var(--gold)] transition-[width] duration-150"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        {/* ================= HERO — Carta da diretora ================= */}
        <section className="ambient-glow grain relative overflow-hidden px-6 py-24 sm:py-32">
          <img
            src="/brand/simbolo-creme.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-32 top-1/2 h-[150%] w-auto -translate-y-1/2 opacity-[0.05] sm:-right-40"
          />
          <div className="relative mx-auto w-full max-w-2xl">
            <BrandMark variant="creme" className="fade-up h-7" />
            <p
              className="type-overline fade-up mt-3 text-gold"
              style={{ animationDelay: "40ms" }}
            >
              Arquitetura de Onboarding
            </p>
            {firstName !== null && (
              <p className="type-caption fade-up mt-2 text-text-tertiary" style={{ animationDelay: "80ms" }}>
                Preparado especialmente para {firstName}
              </p>
            )}
            <h1
              className="type-display fade-up mt-6 text-text-cream"
              style={{ animationDelay: "140ms", fontSize: "clamp(44px, 7vw, 88px)" }}
            >
              {content.carta.title}
            </h1>

            <div
              className="fade-up mt-10 flex flex-col gap-5"
              style={{ animationDelay: "220ms" }}
            >
              {content.carta.paragraphs.map((p, i) => (
                <p key={i} className="type-body leading-relaxed text-text-secondary">
                  {p}
                </p>
              ))}
            </div>

            <p
              className="fade-up mt-10 text-right text-[28px] italic text-text-cream"
              style={{ fontFamily: "var(--font-display)", animationDelay: "300ms" }}
            >
              {content.carta.signature}
            </p>

            {currentStep > 0 && (
              <a
                href="#voce-esta-aqui"
                className="fade-up type-caption mt-14 inline-flex items-center gap-1 text-text-tertiary hover:text-gold"
                style={{ animationDelay: "360ms" }}
              >
                Pular direto para onde você está no roteiro ↓
              </a>
            )}
          </div>
        </section>

        {/* ================= O JEITO UNIK ================= */}
        <section className="px-6 py-20">
          <div className="mx-auto w-full max-w-4xl">
            <p className="type-overline text-gold">{content.jeitoUnik.title}</p>
            <p className="type-body mt-3 max-w-xl text-text-secondary">{content.jeitoUnik.intro}</p>

            <div className="mt-10 grid gap-5 sm:grid-cols-3">
              {content.jeitoUnik.pilares.map((p, i) => (
                <div key={i} className="liquid-glass liquid-glass-interactive rounded-[var(--radius-control-lg)] p-6">
                  <span className="type-display text-[40px] text-gold opacity-60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="type-h3 mt-3 text-text-cream">{p.title}</h3>
                  <p className="type-body-sm mt-3 leading-relaxed text-text-secondary">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= O ROTEIRO — a linha do tempo ================= */}
        <section className="px-6 py-20">
          <div className="mx-auto w-full max-w-2xl">
            <p className="type-overline text-gold">{content.roteiro.title}</p>
            <p className="type-body mt-3 text-text-secondary">{content.roteiro.intro}</p>

            <div className="relative mt-12 flex flex-col gap-12">
              <div className="absolute bottom-2 left-6 top-2 w-px bg-[var(--gold)]/25 sm:left-9" />
              {content.roteiro.passos.map((step, i) => {
                const stepNum = i + 1;
                const isCurrent = stepNum === currentStep;
                const isPast = stepNum < currentStep;
                return (
                  <div
                    key={step.number}
                    id={isCurrent ? "voce-esta-aqui" : undefined}
                    className="relative flex gap-5 scroll-mt-16 sm:gap-7"
                  >
                    <div className="flex w-12 shrink-0 justify-center sm:w-[72px]">
                      <span
                        className={`type-display italic leading-none ${
                          isCurrent
                            ? "text-gold"
                            : isPast
                              ? "text-text-tertiary/60"
                              : "text-text-tertiary/25"
                        }`}
                        style={{ fontSize: "clamp(32px, 5vw, 56px)" }}
                      >
                        {String(stepNum).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-2 sm:pt-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <p className="type-overline text-text-tertiary">Cena {step.number}</p>
                        {isCurrent && (
                          <span className="type-overline rounded-full bg-[var(--accent-soft)] px-2 py-0.5 text-accent">
                            Você está aqui
                          </span>
                        )}
                        {isPast && <span className="type-caption text-gold">✓ concluído</span>}
                      </div>
                      <h3 className={`type-h3 mt-1 ${isCurrent ? "text-text-cream" : "text-text-primary"}`}>
                        {step.title}
                      </h3>
                      <p className="type-body-sm mt-2 leading-relaxed text-text-secondary">{step.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================= AS REGRAS DO SET ================= */}
        <section className="grain px-6 py-20">
          <div className="mx-auto w-full max-w-2xl">
            <p className="type-overline text-gold">{content.regras.title}</p>
            <p className="type-body mt-3 text-text-secondary">{content.regras.intro}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {content.regras.itens.map((it, i) => (
                <div key={i} className="rounded-[var(--radius-control)] border border-border-subtle bg-surface-raised p-4">
                  <p className="type-label text-gold">{it.label}</p>
                  <p className="type-body-sm mt-2 leading-relaxed text-text-secondary">{it.body}</p>
                </div>
              ))}
            </div>

            <p className="type-h3 mt-12 text-text-cream">{content.regras.precisoDeVoceTitle}</p>
            <p className="type-body-sm mt-2 text-text-secondary">{content.regras.precisoDeVoceIntro}</p>
            <div className="mt-6 flex flex-col gap-4">
              {content.regras.precisoDeVoceItens.map((it, i) => (
                <div key={i} className="flex gap-3">
                  <span className="type-overline mt-0.5 text-accent">·</span>
                  <div>
                    <p className="type-label text-text-cream">{it.label}</p>
                    <p className="type-body-sm mt-1 leading-relaxed text-text-secondary">{it.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= DICIONÁRIO UNIK ================= */}
        <section className="px-6 py-20">
          <div className="mx-auto w-full max-w-4xl">
            <p className="type-overline text-gold">{content.dicionario.title}</p>
            <p className="type-body mt-3 max-w-xl text-text-secondary">{content.dicionario.intro}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {content.dicionario.termos.map((t, i) => (
                <div key={i} className="border-l-2 border-[var(--border-default)] pl-4">
                  <p className="type-label text-gold">{t.term}</p>
                  <p className="type-body-sm mt-1 leading-relaxed text-text-secondary">{t.definition}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ENCERRAMENTO ================= */}
        <section className="ambient-glow grain relative overflow-hidden px-6 py-28 text-center">
          <div className="mx-auto w-full max-w-xl">
            <h2 className="text-brand-gradient type-display">{content.encerramento.title}</h2>
            <div className="mt-8 flex flex-col gap-4">
              {content.encerramento.paragraphs.map((p, i) => (
                <p key={i} className="type-body leading-relaxed text-text-secondary">{p}</p>
              ))}
            </div>

            <img
              src="/brand/logotipo-secundario-creme.png"
              alt="Selo Unik — Marca & Posicionamento"
              className="mx-auto mt-12 h-28 w-auto opacity-90"
              style={{ transform: "rotate(-4deg)" }}
            />

            <p
              className="mt-8 text-[24px] italic text-text-cream"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {content.encerramento.signature}
            </p>
            <div className="mt-4 flex justify-center gap-3">
              {content.encerramento.handles.map((h) => (
                <a
                  key={h}
                  href={`https://instagram.com/${h.replace("@", "")}`}
                  target="_blank" rel="noreferrer"
                  className="type-caption text-text-tertiary hover:text-gold"
                >
                  {h}
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Theme>
  );
}
